import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Button, Modal } from '@mui/material';
import './DisputeDetails.css';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DisputeResolveModal from '../../../../../components/Admin/Payments/Disputes/DisputeResolveModel/DisputeResolveModal';
import DisputeRejectModal from '../../../../../components/Admin/Payments/Disputes/DisputeRejectModel/DisputeRejectModal';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { formatDateFull, toLocalTime } from '../../../../../utils/dateUtils';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChatIcon from '@mui/icons-material/Chat';
import KoinLogo from '../../../../../assets/Admin/Dispute/koinlogo.svg';
import { toast } from 'react-hot-toast';
import DisputeDetailsSkeleton from '../../../../../components/SkeletonEffect/DisputeDetailsSkeleton/DisputeDetailsSkeleton';

const UserRoleColor = {
  Student: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
  Teacher: { color: '#BC8D00', bgcolor: '#FFF3CE', value: 'Instructor' },
};

const StatusColor = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  resolved: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Resolved' },
  rejected: { color: '#E53535', bgcolor: '#FFF2F2', value: 'Rejected' },
};

// Format text by replacing underscores with spaces and capitalizing only first letter
const formatText = text => {
  if (!text) return '';
  return text
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/^\w/, char => char.toUpperCase()); // Capitalize only first letter
};

const DisputeDetails = () => {
  const [openResolveModal, setResolveOpen] = useState(false);
  const [openRejectModal, setRejectOpen] = useState(false);
  const [openVideoPlayer, setOpenVideoPlayer] = useState(false);
  const [selectedRecordingIndex, setSelectedRecordingIndex] = useState(0);
  const [disputeDetails, setDisputeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [riyalConversionRate, setRiyalConversionRate] = useState(null);
  const [chatHistory, setChatHistory] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [rbacMe, setRbacMe] = useState(null);

  const navigate = useNavigate();
  const { id } = useParams();

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  // Check if current user can manage disputes
  const canManageDisputes = () => {
    return hasPermission('manage_disputes') || (rbacMe?.roles || []).includes('super_admin');
  };

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  const fetchDisputeDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/admin/dispute/${id}`);
      setDisputeDetails(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dispute details:', err);
      setError('Failed to load dispute details');
      setLoading(false);
    }
  }, [id]);

  // TEMPORARY: Mock multiple recordings for UI testing

  const fetchConversionRate = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/koins-to-riyal');
      setRiyalConversionRate(response.data.conversion_rate);
    } catch (err) {
      console.error('Error fetching riyal conversion rate:', err);
    }
  }, []);

  const fetchChatHistory = useCallback(async () => {
    if (!disputeDetails?.booking?.teacher?.id || !disputeDetails?.students?.[0]?.id) {
      return;
    }

    try {
      setChatLoading(true);
      const response = await axiosInstance.post(
        `/admin/generate-chat-transcript/${disputeDetails.booking.teacher.id}/${disputeDetails.students[0].id}`
      );

      if (response.data.detail && response.data.detail.includes('No chat exists')) {
        setChatHistory({ noChat: true });
      } else if (response.data.pdf_url) {
        setChatHistory({ pdfUrl: response.data.pdf_url });
      } else {
        setChatHistory({ noChat: true });
      }
    } catch (err) {
      console.error('Error fetching chat history:', err);
      setChatHistory({ noChat: true });
    } finally {
      setChatLoading(false);
    }
  }, [disputeDetails]);

  useEffect(() => {
    fetchDisputeDetails();
    fetchConversionRate();
  }, [id, fetchDisputeDetails, fetchConversionRate]);

  useEffect(() => {
    if (disputeDetails) {
      fetchChatHistory();
    }
  }, [disputeDetails, fetchChatHistory]);

  // Convert koins to riyal value
  const convertKoinsToRiyal = koins => {
    if (!riyalConversionRate) return null;
    return (koins * riyalConversionRate).toFixed(2);
  };

  const handleResolve = () => {
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }
    setResolveOpen(true);
  };

  const handleReject = () => {
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }
    setRejectOpen(true);
  };

  const handleOpenVideoPlayer = (index = 0) => {
    setSelectedRecordingIndex(index);
    setOpenVideoPlayer(true);
  };

  const handleCloseVideoPlayer = () => {
    setOpenVideoPlayer(false);
  };

  const handleOpenChatHistory = () => {
    if (chatHistory?.pdfUrl) {
      window.open(chatHistory.pdfUrl, '_blank');
    }
  };

  const submitAction = async (action, comment = '') => {
    if (!canManageDisputes()) {
      toast.dismiss();
      toast.error("You don't have permission to manage disputes");
      return;
    }

    try {
      await axiosInstance.patch(`/admin/dispute/${id}/action/${action}`, { comment });
      // Refresh dispute details after action
      fetchDisputeDetails();
      // Close modals
      setResolveOpen(false);
      setRejectOpen(false);
    } catch (err) {
      console.error(`Error ${action} dispute:`, err);
      toast.error(`Failed to ${action} dispute`);
    }
  };

  if (loading) {
    return (
      <Layout title="Klass Online | Dispute Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <DisputeDetailsSkeleton />
          </Box>
        </Box>
      </Layout>
    );
  }

  if (error || !disputeDetails) {
    return (
      <Layout title="Klass Online | Dispute Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px] flex justify-center items-center">
            <Typography variant="h6" color="error">
              {error || 'Dispute details not found'}
            </Typography>
          </Box>
        </Box>
      </Layout>
    );
  }

  // Get student information from the first item in students array
  const student = disputeDetails.students?.[0] || {};
  const studentCity = student?.city;
  const studentCountry = student?.country;

  const formattedDate = formatDateFull(disputeDetails.created_at);

  const sessionTime = disputeDetails.booking?.start_time
    ? formatDateFull(disputeDetails.booking.start_time)
    : 'N/A';

  const endTime =
    disputeDetails.booking?.start_time && disputeDetails.booking?.length_in_minutes
      ? toLocalTime(disputeDetails.booking.start_time)
          .add(disputeDetails.booking.length_in_minutes, 'minute')
          .format('h:mm A')
      : 'N/A';

  const isInstructorDispute = ['teacher', 'instructor'].includes(
    (disputeDetails.user_role || '').toLowerCase()
  );
  // Calculate refund amount (using session_koins as total)
  const total = disputeDetails.booking?.session_koins || 0;
  const discount = 0; // Not provided in API
  const taxes = 0; // Not provided in API
  const refundTotal = total - discount + taxes;

  return (
    <Layout title="Klass Online | Dispute Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-[18px]">
            <Box className="flex flex-col gap-[5px]">
              <Box className="flex items-center gap-2">
                <Typography
                  className="pointer-text"
                  onClick={() => navigate('/admin/payments/disputes')}
                >
                  Payments {'>'}
                  {'>'} Dispute {'>'}
                  {'>'}
                </Typography>
                <Typography className="view-text">View</Typography>
              </Box>
              <div className="dispute-header">
                <Typography className="dispute-header-title">
                  Dispute ID #{disputeDetails?.id || id || 'N/A'}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    borderRadius: '20px',
                    border: `1px solid ${StatusColor[disputeDetails.status]?.color || '#949DA6'}`,
                    backgroundColor: StatusColor[disputeDetails.status]?.bgcolor || '#F9FAFB',
                    color: StatusColor[disputeDetails.status]?.color || '#949DA6',
                    padding: '2px 8px',
                    maxWidth: 'fit-content',
                  }}
                >
                  <Box
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: StatusColor[disputeDetails.status]?.color || '#949DA6',
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {StatusColor[disputeDetails.status]?.value || disputeDetails.status}
                  </Typography>
                </Box>
              </div>
              <Typography variant="body2" color="textSecondary">
                {formattedDate}
              </Typography>
            </Box>
            <div className="details-section">
              <h3 className="details-title">Personal Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name</span>
                  <span className="info-value">{student?.name || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">User Role</span>
                  <div
                    className="role-tag"
                    style={{
                      backgroundColor: UserRoleColor['Student'].bgcolor,
                      color: UserRoleColor['Student'].color,
                    }}
                  >
                    {UserRoleColor['Student'].value}
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone Number</span>
                  <span className="info-value">{student?.phone_no || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{student?.email || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Current Location</span>
                  <span className="info-value">
                    {(() => {
                      if (studentCity && studentCountry && studentCity !== studentCountry) {
                        return `${studentCity}, ${studentCountry}`;
                      }
                      if (studentCity) {
                        return studentCity;
                      }
                      if (studentCountry) {
                        return studentCountry;
                      }
                      return 'N/A';
                    })()}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Session Type</span>
                  <span className="info-value">
                    {formatText(disputeDetails.booking?.session_type) || 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Instructor Type</span>
                  <span className="info-value">
                    {formatText(disputeDetails.teacher_type) || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="details-section">
              <h3 className="details-title">Dispute Details</h3>

              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Reason</span>
                  <span className="info-value">{formatText(disputeDetails.reason)}</span>
                </div>

                {disputeDetails.description && (
                  <div className="info-item">
                    <span className="info-label">Description</span>
                    <span className="info-value">{disputeDetails.description}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Session Recording and Document Section */}
            <div className="details-section">
              <h3 className="details-title">Session Recording</h3>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                {disputeDetails.booking?.recording_url &&
                Array.isArray(disputeDetails.booking.recording_url) &&
                disputeDetails.booking.recording_url.length > 0 ? (
                  disputeDetails.booking.recording_url.map((recording, index) => (
                    <div
                      key={index}
                      className="recording-item"
                      onClick={() => handleOpenVideoPlayer(index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <Box
                        sx={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '4px',
                          backgroundColor: '#E0EFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <VideoLibraryIcon sx={{ color: '#0A5EF3' }} />
                      </Box>
                      <span
                        style={{
                          color: '#111827',
                          fontSize: '14px',
                          fontFamily: 'Poppins',
                          fontWeight: '400',
                        }}
                      >
                        Session Recording{' '}
                        {disputeDetails.booking.recording_url.length > 1 ? `(${index + 1})` : ''}
                      </span>
                    </div>
                  ))
                ) : disputeDetails.booking?.recording_url &&
                  !Array.isArray(disputeDetails.booking.recording_url) ? (
                  <div
                    className="recording-item"
                    onClick={() => handleOpenVideoPlayer(0)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <Box
                      sx={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '4px',
                        backgroundColor: '#E0EFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <VideoLibraryIcon sx={{ color: '#0A5EF3' }} />
                    </Box>
                    <span
                      style={{
                        color: '#111827',
                        fontSize: '14px',
                        fontFamily: 'Poppins',
                        fontWeight: '400',
                      }}
                    >
                      Session Recording
                    </span>
                  </div>
                ) : (
                  <div
                    className="recording-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <span
                      style={{
                        color: '#111827',
                        fontSize: '14px',
                        fontFamily: 'Poppins',
                        fontWeight: '400',
                      }}
                    >
                      No recording available
                    </span>
                  </div>
                )}

                {disputeDetails.file_url && (
                  <div
                    className="recording-item"
                    onClick={() => window.open(disputeDetails.file_url, '_blank')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <Box
                      sx={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '4px',
                        backgroundColor: '#E0EFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PictureAsPdfIcon sx={{ color: '#0A5EF3' }} />
                    </Box>
                    <span
                      style={{
                        color: '#111827',
                        fontSize: '14px',
                        fontFamily: 'Poppins',
                        fontWeight: '400',
                      }}
                    >
                      Document
                    </span>
                  </div>
                )}
              </Box>
            </div>

            {/* Chat History Section */}
            <div className="details-section">
              <h3 className="details-title">Chat History</h3>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                {chatLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <Box
                      sx={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '4px',
                        backgroundColor: '#E0EFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background:
                            'linear-gradient(90deg, #B8D9FF 25%, #A0CBFF 50%, #B8D9FF 75%)',
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 1.5s infinite linear',
                          '@keyframes shimmer': {
                            '0%': { backgroundPosition: '-200% 0' },
                            '100%': { backgroundPosition: '200% 0' },
                          },
                        }}
                      />
                    </Box>
                    <span className="chat-history-loading">Loading chat history...</span>
                  </div>
                ) : chatHistory?.noChat ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <Box
                      sx={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '4px',
                        backgroundColor: '#FEF2F2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ChatIcon sx={{ color: '#DC2626' }} />
                    </Box>
                    <span className="chat-history-no-chat">
                      No chat exists between the specified Learner and Instructor.
                    </span>
                  </div>
                ) : chatHistory?.pdfUrl ? (
                  <div className="chat-history-item" onClick={handleOpenChatHistory}>
                    <div className="chat-history-content">
                      <Box
                        className="chat-history-icon"
                        sx={{
                          backgroundColor: '#E0EFFF',
                        }}
                      >
                        <ChatIcon sx={{ color: '#0A5EF3' }} />
                      </Box>
                      <span className="chat-history-text">Chat History</span>
                    </div>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#0A5EF3',
                        color: '#0A5EF3',
                        fontSize: '12px',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        fontWeight: '400',
                        '&:hover': {
                          borderColor: '#0A5EF3',
                          backgroundColor: '#F0F7FF',
                        },
                      }}
                    >
                      View
                    </Button>
                  </div>
                ) : null}
              </Box>
            </div>

            {/* Refund Details */}
            <div className="details-section">
              <h3 className="details-title">Refund Details</h3>

              {!canManageDisputes() && disputeDetails.status === 'pending' && (
                <Box
                  sx={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#856404',
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      lineHeight: '1.5',
                      margin: 0,
                    }}
                  >
                    <strong>Note:</strong> You don't have permission to approve or deny disputes.
                    Only users with dispute management permissions can take action on pending
                    disputes.
                  </Typography>
                </Box>
              )}

              <div className="refund-item">
                <div className="refund-details">
                  <span className="detail-subtitle">
                    {formatText(disputeDetails.booking?.title) || 'Session'} - By{' '}
                    {formatText(disputeDetails.booking?.teacher?.name) || 'N/A'}
                  </span>
                  <span className="refund-time">
                    {sessionTime} - {endTime}
                  </span>
                </div>
                {!isInstructorDispute && (
                  <div className="amount-value">
                    {/* <img src={KoinLogo} alt="koin" />
                    <span>{total}</span> */}
                    {riyalConversionRate && (
                      <span className="riyal-value">{convertKoinsToRiyal(total)} Riyal</span>
                    )}
                  </div>
                )}
              </div>

              {!isInstructorDispute && (
                <div className="amount-breakdown">
                  <div className="amount-row">
                    <span>Subtotal</span>
                    <div className="amount-value">
                      {/* <img src={KoinLogo} alt="koin" />
                      <span>{total}</span> */}
                      {riyalConversionRate && (
                        <span className="riyal-value">{convertKoinsToRiyal(total)} Riyal</span>
                      )}
                    </div>
                  </div>
                  <div className="amount-row">
                    <span>Discount</span>
                    <div className="amount-value">
                      {/* <img src={KoinLogo} alt="koin" />
                      <span>{discount}</span> */}
                      {riyalConversionRate && (
                        <span className="riyal-value">{convertKoinsToRiyal(discount)} Riyal</span>
                      )}
                    </div>
                  </div>
                  <div className="amount-row">
                    <span>Taxes</span>
                    <div className="amount-value">
                      {/* <img src={KoinLogo} alt="koin" />
                      <span>{taxes}</span> */}
                      {riyalConversionRate && (
                        <span className="riyal-value">{convertKoinsToRiyal(taxes)} Riyal</span>
                      )}
                    </div>
                  </div>
                  <div className="amount-row total-row">
                    <span>Refund Total</span>
                    <div className="amount-value">
                      {/* <img src={KoinLogo} alt="koin" />
                      <span>{refundTotal}</span> */}
                      {riyalConversionRate && (
                        <span className="riyal-value">
                          {convertKoinsToRiyal(refundTotal)} Riyal
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {disputeDetails.status === 'pending' && (
                <div className="action-buttons">
                  <Button
                    variant="outlined"
                    disabled={!canManageDisputes()}
                    sx={{
                      width: 100,
                      background: 'white',
                      borderRadius: 60,
                      border: '1px #949DA6 solid',
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'inline-flex',
                      opacity: canManageDisputes() ? 1 : 0.6,
                    }}
                    style={{
                      color: '#949DA6',
                      fontSize: '14px',
                      paddingX: 15,
                      paddingY: 8,
                      fontFamily: 'Poppins',
                      fontWeight: 400,
                      wordWrap: 'break-word',
                    }}
                    onClick={handleReject}
                    title={
                      !canManageDisputes()
                        ? "You don't have permission to manage disputes"
                        : 'Deny dispute'
                    }
                  >
                    Deny
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!canManageDisputes()}
                    sx={{
                      width: 100,
                      background: '#0A5EF3',
                      borderRadius: 60,
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'inline-flex',
                      opacity: canManageDisputes() ? 1 : 0.6,
                    }}
                    style={{
                      color: 'white',
                      fontSize: 14,
                      paddingX: 10,
                      paddingY: 8,
                      fontFamily: 'Poppins',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                    onClick={handleResolve}
                    title={
                      !canManageDisputes()
                        ? "You don't have permission to manage disputes"
                        : 'Approve dispute'
                    }
                  >
                    Approve
                  </Button>
                </div>
              )}
            </div>
          </Box>
        </Box>

        {openResolveModal && (
          <DisputeResolveModal
            setResolveOpen={setResolveOpen}
            onResolve={comment => submitAction('resolve', comment)}
            userRole={disputeDetails.user_role || 'student'}
          />
        )}

        {openRejectModal && (
          <DisputeRejectModal
            setRejectOpen={setRejectOpen}
            onReject={comment => submitAction('reject', comment)}
            userRole={disputeDetails.user_role || 'student'}
          />
        )}

        {/* Video Player Modal */}
        <Modal
          open={openVideoPlayer}
          onClose={handleCloseVideoPlayer}
          aria-labelledby="video-player-modal"
          aria-describedby="session-recording-player"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              maxWidth: '800px',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 2,
              borderRadius: '8px',
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography
                id="video-player-modal"
                variant="h6"
                component="h2"
                sx={{ fontFamily: 'Poppins' }}
              >
                Session Recording
              </Typography>
              <Button
                onClick={handleCloseVideoPlayer}
                sx={{
                  minWidth: 'auto',
                  p: 0.5,
                  color: '#949DA6',
                }}
              >
                ✕
              </Button>
            </Box>
            <Box sx={{ width: '100%', position: 'relative', paddingTop: '56.25%' }}>
              {(() => {
                const recordingUrl = Array.isArray(disputeDetails.booking?.recording_url)
                  ? disputeDetails.booking.recording_url[selectedRecordingIndex]
                  : disputeDetails.booking?.recording_url;

                return recordingUrl?.includes('youtube.com') ? (
                  <iframe
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '4px',
                    }}
                    src={recordingUrl.replace('watch?v=', 'embed/')}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '4px',
                      objectFit: 'contain',
                      backgroundColor: '#000',
                    }}
                    src={recordingUrl}
                    controls
                    autoPlay
                  />
                );
              })()}
            </Box>
          </Box>
        </Modal>
      </Box>
    </Layout>
  );
};

export default DisputeDetails;
